import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconTrashCan from "../../../../img/icons/trash-can.svg";
import { MAX_BASKET_LINE_QUANTITY } from "../../../constants";
import { IBasket, IBasketLine } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { BasketLineVariant } from "../constants";
import { BasketLineImage } from "./BasketLineImage";
import { BasketLinePrice } from "./BasketLinePrice";
import { BasketLineTitle } from "./BasketLineTitle";

import styles from "./BasketLineMinimal.module.scss";

const RemoveButton = (
    props: Immutable<{
        onRemoveLine: (event?: React.MouseEvent<HTMLElement>) => void;
    }>,
) => (
    <button
        aria-label={t`Remove`}
        onClick={props.onRemoveLine}
        className={`${styles.button} al-basket-line__button--remove`}
    >
        <SVG className={styles.icon} src={iconTrashCan} aria-hidden="true" />
    </button>
);

const QuantitySelector = (
    props: Immutable<{
        line: IBasketLine;
        onUpdateLineQuantity: (
            event: React.FormEvent<HTMLSelectElement>,
        ) => void;
    }>,
): React.ReactNode => {
    const line = props.line;
    if (!line.quantity) {
        return null;
    }
    const options = [];
    for (let i = 0; i <= MAX_BASKET_LINE_QUANTITY; i++) {
        options.push(
            <option key={i} value={i}>
                {i}
            </option>,
        );
    }
    return (
        <div className={styles.qtyContainer}>
            <label className={styles.qtyLabel} htmlFor="product_quantity">
                {t`Qty:`} &nbsp;
            </label>
            <select
                value={line.quantity}
                onChange={props.onUpdateLineQuantity}
                id="product_quantity"
            >
                {options}
            </select>
        </div>
    );
};

type IProps = Immutable<{
    line: IBasketLine;
    basket: IBasket;
    variant: BasketLineVariant;
    onRemoveLine: (event?: React.MouseEvent<HTMLElement>) => void;
    onUpdateLineQuantity: (event: React.FormEvent<HTMLSelectElement>) => void;
}> & {
    removeBasketLine: (lineURL: IBasketLineAPIURL) => Promise<IBasket | null>;
    updateBasketLineQuantity: (
        lineURL: IBasketLineAPIURL,
        newQty: number,
    ) => Promise<IBasket | null>;
};

export const BasketLineMinimalEnhanced = (props: IProps): React.ReactNode => (
    <li>
        {/* TODO(i18n): Translate aria-label using t tagged template: aria-label={t`product`} */}
        <div className={styles.line} role="group" aria-label="product">
            <BasketLineImage product={props.line.product} />
            <div className={styles.titleContainer}>
                <BasketLineTitle
                    product={props.line.product}
                    additionalCSSClass={styles.title}
                />
                <QuantitySelector
                    line={props.line}
                    onUpdateLineQuantity={props.onUpdateLineQuantity}
                />
            </div>
            <div className={styles.pricingContainer}>
                {props.line.price_excl_tax_excl_discounts && (
                    <BasketLinePrice
                        line={props.line}
                        basket={props.basket}
                        isMinimal={true}
                        variant={props.variant}
                    />
                )}
                <RemoveButton onRemoveLine={props.onRemoveLine} />
            </div>
        </div>
    </li>
);
